import type { AuthSession } from './types';

type SessionLike = Pick<AuthSession, 'onboardingCompleted'>;

export type AuthBootstrapState =
  | 'signed_out'
  | 'recovering'
  | 'error_terminal'
  | 'authenticated_onboarding'
  | 'authenticated_ready';

export type AuthenticatedBootstrapState = Extract<
  AuthBootstrapState,
  'authenticated_onboarding' | 'authenticated_ready'
>;

export const hasCompletedOnboarding = (sessionLike: SessionLike): boolean => {
  return sessionLike.onboardingCompleted === true;
};

export const resolveSessionBootstrapState = (
  sessionLike: SessionLike,
): AuthenticatedBootstrapState =>
  hasCompletedOnboarding(sessionLike) ? 'authenticated_ready' : 'authenticated_onboarding';

export const resolvePostAuthRedirect = (input: {
  intendedPath: string;
  bootstrapState: AuthBootstrapState;
  fallback?: string;
}): string => {
  const fallback = input.fallback ?? '/execution-runs';
  if (input.bootstrapState === 'authenticated_onboarding') {
    return '/onboarding';
  }
  if (
    input.bootstrapState === 'authenticated_ready' &&
    (input.intendedPath === '/onboarding' || input.intendedPath.startsWith('/onboarding?'))
  ) {
    return fallback;
  }
  return input.intendedPath;
};

export const resolveAuthBootstrapState = (input: {
  session: AuthSession | null;
  authLoading: boolean;
  manualSignOut: boolean;
  hasRecoverableSessionSignal: boolean;
  hasTerminalAuthError?: boolean;
}): AuthBootstrapState => {
  if (input.session) {
    return resolveSessionBootstrapState(input.session);
  }
  if (input.manualSignOut) return 'signed_out';
  if (input.hasTerminalAuthError) return 'error_terminal';
  if (input.authLoading || input.hasRecoverableSessionSignal) return 'recovering';
  return 'signed_out';
};
